import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from 'ui';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { sortCOPSModelToParam, sortParamToCOPSModel } from 'utils/helpers';
import { setOpenFilterId, updateSort } from '../../worklist/containers/worklist-actions';
import { sortTypes } from '../../utils/app-configs';

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  height: 100%;
`;

const Name = styled.div`
  left: 0;
`;

const Icons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 5px;
  position: absolute;
  right: 0;
  background-color: #F8F8F8;
  padding-left: 5px;
`;

const SortIcon = styled.div`
  display: flex;
  flex-flow: row nowrap;
  cursor: pointer;
  align-items: center;
  i {
    cursor: pointer;
    color: black;
  }
`;

const SortIndex = styled.p`
  font-weight: 300;
  font-size: 12px;
  margin-right: 3px;
`;

const MenuIcon = styled.div`
  cursor: pointer;
  i {
    cursor: pointer;
    color: black;
  }
`;

const FilterIcon = styled.div`
  i {
    color: black;
  }
`;

const getSortIcon = (icon) => {
  const iconClass = icon.replace('<i class="', '').replace('"></i>', '');
  return <Icon className={iconClass} />;
};

const CustomHeader = ({
  column,
  enableSorting,
  showColumnMenu,
  displayName,
}) => {
  const [sortOption, setSortOption] = useState();
  const [sortOptions, setSortOptions] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const {
    activeSort,
    activeFilters,
    openFilterId,
  } = useSelector(state => state.worklist);
  const { defaultSorting } = useSelector(state => state.worklist.metaData);
  const refButton = useRef(null);

  useEffect(() => {
    setSortOptions(sortParamToCOPSModel(activeSort || defaultSorting));
  }, [activeSort]);

  useDeepCompareEffect(() => {
    setSortOption(
      sortOptions?.find(o => o?.sortColumn?.toLowerCase() === column?.colId?.toLowerCase()),
    );
  }, [sortOptions]);

  useEffect(() => {
    if (showMenu && openFilterId !== column.colDef.filterId) {
      setShowMenu(false);
      setMenuOpen(false);
    }
  }, [openFilterId]);

  const handleSortChange = () => {
    if (!enableSorting) {
      return;
    }
    const id = column.colId;
    let options = sortOptions;
    if (!sortOption) {
      options = [
        ...options,
        {
          sortColumn: id,
          sortIndex: options.length,
          sortDirection: sortTypes.ascending,
        },
      ];
    } else if (sortOption.sortDirection === sortTypes.ascending) {
      options = options?.map((o) => {
        if (o.sortColumn?.toLowerCase() === id?.toLowerCase()) {
          o.sortDirection = sortTypes.descending;
        }
        return o;
      });
    } else if (sortOptions.length === 1) {
      options = sortOptions?.map((o) => {
        if (o.sortColumn?.toLowerCase() === id?.toLowerCase()) {
          o.sortDirection = sortTypes.ascending;
        }
        return o;
      });
    } else {
      options = options
        ?.filter(o => o.sortColumn?.toLowerCase() !== id?.toLowerCase())
        ?.sort((a, b) => a.sortIndex - b.sortIndex)
        ?.map((o) => {
          if (o.sortIndex > sortOption.sortIndex) {
            o.sortIndex -= 1;
          }
          return o;
        });
    }
    dispatch(updateSort(sortCOPSModelToParam(options)));
  };

  const renderMenuIcon = () => {
    if (column?.colDef?.filterId && showMenu) {
      return <MenuIcon
        ref={refButton}
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(true);
          dispatch(setOpenFilterId(column.colDef.filterId));
          showColumnMenu(refButton.current);
        }}
      >
        <Icon className='fa-light fa-bars' />
      </MenuIcon>;
    }
    return null;
  };

  const renderSortIcon = () => {
    if (enableSorting) {
      if (sortOption) {
        const { icons } = column.colDef;
        return <SortIcon>
          <SortIndex>({sortOption.sortIndex + 1})</SortIndex>
          {getSortIcon(sortOption.sortDirection === sortTypes.ascending ?
            icons.sortAscending : icons.sortDescending)}
        </SortIcon>;
      }
    }
    return null;
  };

  const renderFilterIcon = () => {
    if (Object.keys(activeFilters || {})?.find(k => k === column.colDef.filterId)) {
      if (activeFilters[column.colDef.filterId]?.length) {
        return <FilterIcon><Icon className='fa-regular fa-filter' /></FilterIcon>;
      }
    }
    return null;
  };

  return (
    <Header
      onClick={handleSortChange}
      onMouseOver={() => setShowMenu(true)}
      onMouseLeave={() => !menuOpen && setShowMenu(false)}
    >
      <Name data-test-id='lbl-column-header'>{displayName}</Name>
      <Icons>
        {renderSortIcon()}
        {renderFilterIcon()}
        {renderMenuIcon()}
      </Icons>
    </Header >
  );
};

CustomHeader.propTypes = {
  column: PropTypes.object,
  enableSorting: PropTypes.bool,
  showColumnMenu: PropTypes.func,
  displayName: PropTypes.string,
};

export default CustomHeader;
